import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Calendar, User, Search, Plus, Youtube, ExternalLink, Bell, Info } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ScriptureHeader from '../components/ScriptureHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// YouTube Channel Configuration - Living Fruit Ministry
const YOUTUBE_CONFIG = {
  channelUrl: 'https://www.youtube.com/channel/UCZ8ewM6AKFbobbU6TscphoA',
  channelId: 'UCZ8ewM6AKFbobbU6TscphoA',
  channelName: 'Living Fruit Ministry',
  bannerImage: 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png'
};

export default function SermonsPage() {
  const [sermons, setSermons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSermon, setSelectedSermon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [newSermon, setNewSermon] = useState({
    title: '',
    description: '',
    video_url: '',
    speaker: 'Pastor',
    date: '',
    thumbnail: ''
  });

  useEffect(() => {
    fetchSermons();
  }, []);

  const fetchSermons = async () => {
    try {
      const response = await axios.get(`${API}/sermons`);
      setSermons(response.data);
    } catch (error) {
      console.error('Error fetching sermons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSermon = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/sermons`, newSermon);
      toast.success('Sermon added successfully!');
      setShowAddForm(false);
      setNewSermon({ title: '', description: '', video_url: '', speaker: 'Pastor', date: '', thumbnail: '' });
      fetchSermons();
    } catch (error) {
      toast.error('Failed to add sermon');
    }
  };

  // Convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    // Handle various YouTube URL formats
    let videoId = '';
    
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      return url; // Already an embed URL
    } else if (url.includes('vimeo.com')) {
      // Handle Vimeo URLs
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}`;
      }
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    return url;
  };

  // Get YouTube thumbnail from video URL
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    
    let videoId = '';
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  };

  const filteredSermons = sermons.filter(sermon =>
    sermon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sermon.speaker?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Placeholder sermons for display
  const displaySermons = filteredSermons.length > 0 ? filteredSermons : [
    { id: '1', title: 'The Fruit of the Spirit', speaker: 'Pastor Liza', date: '2024-12-01', description: 'Exploring Galatians 5:22-23 and how we can cultivate spiritual fruit in our lives.', video_url: '' },
    { id: '2', title: 'Living as Salt and Light', speaker: 'Co-Pastor Bernardo', date: '2024-11-24', description: 'Understanding our calling to be positive influences in our communities.', video_url: '' },
    { id: '3', title: 'Faith in Action', speaker: 'Pastor Liza', date: '2024-11-17', description: 'How faith without works is incomplete - practical steps to live out your beliefs.', video_url: '' },
  ];

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="sermons-page">
      {/* Scripture Header */}
      <ScriptureHeader page="sermons" />

      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal mb-4 block">Watch & Listen</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Sermons</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto mb-8">
              Grow in faith through our collection of powerful messages. New sermons added weekly.
            </p>
            
            {/* YouTube Subscribe Section */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              {YOUTUBE_CONFIG.channelUrl ? (
                <a
                  href={YOUTUBE_CONFIG.channelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF0000] text-white px-6 py-3 rounded-full hover:bg-[#cc0000] transition-colors font-medium"
                  data-testid="youtube-subscribe"
                >
                  <Youtube size={20} />
                  Subscribe on YouTube
                </a>
              ) : (
                <div className="inline-flex items-center gap-2 bg-[#FF0000] text-white px-6 py-3 rounded-full opacity-80">
                  <Youtube size={20} />
                  Subscribe on YouTube
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Coming Soon</span>
                </div>
              )}
              
              <button
                onClick={() => setShowSetupGuide(true)}
                className="inline-flex items-center gap-2 text-charcoal/60 hover:text-charcoal transition-colors text-sm"
              >
                <Info size={16} />
                Setup Guide
              </button>
            </div>
          </motion.div>

          {/* YouTube Channel Embed Placeholder */}
          {YOUTUBE_CONFIG.channelId ? (
            <div className="mb-12 bg-white rounded-2xl p-6 border border-gold/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl text-charcoal flex items-center gap-2">
                  <Youtube className="text-[#FF0000]" size={24} />
                  Latest from Our Channel
                </h2>
                <a
                  href={YOUTUBE_CONFIG.channelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#FF0000] hover:underline flex items-center gap-1"
                >
                  View Channel <ExternalLink size={14} />
                </a>
              </div>
              <div className="aspect-video rounded-xl overflow-hidden bg-charcoal/5">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed?listType=uploads&list=${YOUTUBE_CONFIG.channelId}`}
                  title="Living Fruit Ministry YouTube Channel"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="text-center text-sm text-charcoal/50 mt-4">
                Don't see videos? <a href={YOUTUBE_CONFIG.channelUrl} target="_blank" rel="noopener noreferrer" className="text-[#FF0000] hover:underline">Visit our YouTube channel</a> to see all content.
              </p>
            </div>
          ) : (
            <div className="mb-12 bg-gradient-to-r from-red-50 to-rose-50 rounded-2xl p-8 border border-red-100 text-center">
              <Youtube className="mx-auto text-[#FF0000] mb-4" size={48} />
              <h2 className="font-serif text-xl text-charcoal mb-2">YouTube Channel Coming Soon</h2>
              <p className="text-charcoal/60 text-sm max-w-md mx-auto mb-4">
                We're setting up our YouTube channel to bring you video sermons. Subscribe to be notified when we launch!
              </p>
              <button
                onClick={() => setShowSetupGuide(true)}
                className="text-[#FF0000] hover:underline text-sm font-medium"
              >
                Staff: View Setup Instructions →
              </button>
            </div>
          )}

          {/* Search & Add */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-8">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
              <Input
                type="text"
                placeholder="Search sermons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-gold/20 focus:border-gold"
                data-testid="sermon-search"
              />
            </div>
            
            <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
              <DialogTrigger asChild>
                <Button className="bg-burgundy hover:bg-purple text-white" data-testid="add-sermon-btn">
                  <Plus size={18} className="mr-2" /> Add Sermon
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Add New Sermon</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSermon} className="space-y-4 mt-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={newSermon.title}
                      onChange={(e) => setNewSermon({...newSermon, title: e.target.value})}
                      required
                      data-testid="sermon-title-input"
                    />
                  </div>
                  <div>
                    <Label>Speaker</Label>
                    <Input
                      value={newSermon.speaker}
                      onChange={(e) => setNewSermon({...newSermon, speaker: e.target.value})}
                      data-testid="sermon-speaker-input"
                    />
                  </div>
                  <div>
                    <Label>YouTube or Vimeo URL</Label>
                    <Input
                      value={newSermon.video_url}
                      onChange={(e) => setNewSermon({...newSermon, video_url: e.target.value})}
                      placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                      required
                      data-testid="sermon-video-input"
                    />
                    <p className="text-xs text-charcoal/50 mt-1">
                      Paste your YouTube or Vimeo video link
                    </p>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={newSermon.date}
                      onChange={(e) => setNewSermon({...newSermon, date: e.target.value})}
                      required
                      data-testid="sermon-date-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newSermon.description}
                      onChange={(e) => setNewSermon({...newSermon, description: e.target.value})}
                      rows={3}
                      data-testid="sermon-description-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-burgundy hover:bg-purple" data-testid="submit-sermon-btn">
                    Add Sermon
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Sermon Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displaySermons.map((sermon, i) => {
              const thumbnail = getYouTubeThumbnail(sermon.video_url || sermon.vimeo_url);
              return (
                <motion.div
                  key={sermon.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden border border-gold/10 hover:border-gold/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => (sermon.video_url || sermon.vimeo_url) && setSelectedSermon(sermon)}
                  data-testid={`sermon-card-${i}`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-charcoal/5 relative flex items-center justify-center overflow-hidden">
                    {thumbnail ? (
                      <>
                        <img 
                          src={thumbnail} 
                          alt={sermon.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <div className="w-16 h-16 bg-[#FF0000] rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                            <Play size={24} className="text-white ml-1" />
                          </div>
                        </div>
                      </>
                    ) : (sermon.video_url || sermon.vimeo_url) ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-burgundy/10">
                        <div className="w-16 h-16 bg-burgundy rounded-full flex items-center justify-center">
                          <Play size={24} className="text-white ml-1" />
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Play size={24} className="text-gold ml-1" />
                        </div>
                        <span className="text-xs text-charcoal/50">Video coming soon</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-5">
                    <h3 className="font-serif text-lg text-charcoal mb-2 line-clamp-2">{sermon.title}</h3>
                    <p className="text-charcoal/60 text-sm mb-3 line-clamp-2">{sermon.description}</p>
                    <div className="flex items-center gap-4 text-xs text-charcoal/50">
                      <span className="flex items-center gap-1">
                        <User size={14} /> {sermon.speaker}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} /> {sermon.date}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          
          {displaySermons.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-charcoal/50">No sermons found. Add your first sermon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Modal */}
      {selectedSermon && (
        <Dialog open={!!selectedSermon} onOpenChange={() => setSelectedSermon(null)}>
          <DialogContent className="bg-white max-w-4xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">{selectedSermon.title}</DialogTitle>
            </DialogHeader>
            <div className="aspect-video mt-4 rounded-xl overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                src={getYouTubeEmbedUrl(selectedSermon.video_url || selectedSermon.vimeo_url)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedSermon.title}
              />
            </div>
            <p className="text-charcoal/70 mt-4">{selectedSermon.description}</p>
          </DialogContent>
        </Dialog>
      )}

      {/* Setup Guide Modal */}
      <Dialog open={showSetupGuide} onOpenChange={setShowSetupGuide}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Youtube className="text-[#FF0000]" />
              YouTube Setup Guide
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Step 1 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 1: Create Your YouTube Channel</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Go to <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="text-[#FF0000] hover:underline">youtube.com</a></li>
                <li>Sign in with your Google account</li>
                <li>Click your profile icon → "Create a channel"</li>
                <li>Choose "Use a custom name" and enter "Living Fruit Ministry"</li>
                <li>Complete the channel setup</li>
              </ol>
            </div>

            {/* Step 2 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 2: Get Your Channel URL</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Go to your channel page</li>
                <li>Click "Customize channel"</li>
                <li>Go to "Basic info" tab</li>
                <li>Copy your channel URL (e.g., youtube.com/@LivingFruitMinistry)</li>
              </ol>
            </div>

            {/* Step 3 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 3: Upload a Sermon Video</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Click the "Create" button (camera icon with +)</li>
                <li>Select "Upload video"</li>
                <li>Choose your sermon video file</li>
                <li>Add title, description, and thumbnail</li>
                <li>Set visibility to "Public"</li>
                <li>Copy the video URL after publishing</li>
              </ol>
            </div>

            {/* Step 4 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Step 4: Add Video to Website</h3>
              <ol className="list-decimal list-inside text-sm text-charcoal/70 space-y-2">
                <li>Come back to this Sermons page</li>
                <li>Click "Add Sermon"</li>
                <li>Paste your YouTube video URL</li>
                <li>Fill in the title, speaker, and date</li>
                <li>Click "Add Sermon" to publish</li>
              </ol>
            </div>

            {/* Example URLs */}
            <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="font-semibold text-charcoal mb-2">Supported Video URL Formats:</h3>
              <ul className="text-sm text-charcoal/70 space-y-1 font-mono">
                <li>• https://www.youtube.com/watch?v=VIDEO_ID</li>
                <li>• https://youtu.be/VIDEO_ID</li>
                <li>• https://vimeo.com/VIDEO_ID</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
